import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Chip from '@mui/joy/Chip';
import Button from '@mui/joy/Button';
import IconButton from '@mui/joy/IconButton';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import CircularProgress from '@mui/joy/CircularProgress';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton from '@mui/joy/ListItemButton';
import Tooltip from '@mui/joy/Tooltip';
import Switch from '@mui/joy/Switch';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import {
  fetchRegistrationTraceIndex,
  fetchRegistrationTraceEntry,
  fetchRegistrationTracePcap,
} from '../../api/registrations';
import { traceEntryToText, describeTraceError, describeEvictions, entryLabel, formatTimestamp } from './traceText';

/** Outcome words the node uses, mapped onto the palette the rest of the panel uses for status. */
function outcomeColor(outcome) {
  switch (String(outcome || '').toLowerCase()) {
    case 'success':
    case 'completed':
      return 'success';
    case 'failure':
      return 'danger';
    case 'in-progress':
    case 'active':
      return 'primary';
    default:
      return 'neutral';
  }
}

/** Hand a blob to the browser as a download, and let go of the object URL straight after. */
function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function registrationLabel(registration) {
  if (!registration) return 'Registration';
  if (registration.name) return registration.name;
  if (registration.username) {
    return registration.registrar ? `${registration.username}@${registration.registrar}` : registration.username;
  }
  return registration.id || 'Registration';
}

/**
 * The SIP conversation for one registration, as text.
 *
 * Two fetches, because the API is deliberately split that way: an index listing
 * what the owning b2bua node currently holds — cheap, and all a list needs —
 * and then the messages of whichever exchange was opened, which run to tens of
 * kilobytes each and are not worth pulling for exchanges nobody reads.
 */
export default function RegistrationTraceModal({ open, registration, onClose, setError, setInform }) {
  const registrationId = registration?.id || null;

  const [index, setIndex] = useState(null);
  const [indexLoading, setIndexLoading] = useState(false);
  const [indexError, setIndexError] = useState(null);

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  /** Which pcap request is in flight, if any: 'entry' | 'registration' | null. */
  const [pcapPending, setPcapPending] = useState(null);

  /**
   * Whether to ask the node for the platform leg as well as the customer one.
   *
   * Off by default, because the customer leg is the half a customer can compare
   * against their own PBX logs and the other half reads as a second call to
   * anyone who does not know there is a b2bua in the path. On, it is the half
   * that says why a call the customer watched fail actually failed.
   */
  const [debug, setDebug] = useState(false);

  const entries = useMemo(() => index?.transactions || [], [index]);

  const loadIndex = useCallback(async () => {
    if (!registrationId) return;
    setIndexLoading(true);
    setIndexError(null);
    try {
      const data = await fetchRegistrationTraceIndex(registrationId);
      setIndex(data);
      // Open the newest exchange straight away: the index is a means to the
      // trace, not the thing anybody came here to read.
      setSelectedId((prev) => {
        const list = data?.transactions || [];
        if (prev && list.some((entry) => entry.id === prev)) return prev;
        return list[0]?.id || null;
      });
    } catch (err) {
      setIndex(null);
      setSelectedId(null);
      setIndexError(describeTraceError(err));
    } finally {
      setIndexLoading(false);
    }
  }, [registrationId]);

  useEffect(() => {
    if (!open) return;
    setIndex(null);
    setSelectedId(null);
    setDetail(null);
    setDetailError(null);
    loadIndex();
  }, [open, loadIndex]);

  useEffect(() => {
    if (!open || !registrationId || !selectedId) {
      setDetail(null);
      return undefined;
    }
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    (async () => {
      try {
        const data = await fetchRegistrationTraceEntry(registrationId, selectedId, { debug });
        if (!cancelled) setDetail(data);
      } catch (err) {
        if (!cancelled) {
          setDetail(null);
          setDetailError(describeTraceError(err));
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, registrationId, selectedId, debug]);

  const text = useMemo(
    () => (detail ? traceEntryToText(detail, { registration }) : ''),
    [detail, registration],
  );

  const copyText = useCallback(async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setInform?.({ success: 'SIP trace copied to clipboard.' });
    } catch {
      setError?.('Could not copy to clipboard.');
    }
  }, [text, setError, setInform]);

  const downloadText = useCallback(() => {
    if (!text) return;
    saveBlob(
      new Blob([text], { type: 'text/plain;charset=utf-8' }),
      `sip-trace-${registrationId || 'registration'}-${selectedId || 'exchange'}.txt`,
    );
  }, [text, registrationId, selectedId]);

  /**
   * The capture file, for Wireshark or sngrep. Unlike the text document — which
   * is rendered from what has already been fetched — this is a separate request
   * to the node, at whichever scope was asked for.
   */
  const downloadPcap = useCallback(
    async (scope) => {
      const transactionId = scope === 'entry' ? selectedId : null;
      if (!registrationId || (scope === 'entry' && !transactionId)) return;
      setPcapPending(scope);
      try {
        const blob = await fetchRegistrationTracePcap(registrationId, transactionId, { debug });
        saveBlob(blob, `sip-trace-${registrationId}${transactionId ? `-${transactionId}` : ''}.pcap`);
      } catch (err) {
        setError?.(describeTraceError(err));
      } finally {
        setPcapPending(null);
      }
    },
    [registrationId, selectedId, debug, setError],
  );

  const evicted = describeEvictions(index?.evictions);
  const keepalive = index?.keepalive;

  return (
    <Modal open={!!open} onClose={onClose}>
      <ModalDialog
        sx={{
          maxWidth: 920,
          width: '100%',
          top: '4rem',
          transform: 'translateX(-50%)',
          maxHeight: 'calc(100dvh - 6rem)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <ModalClose onClick={onClose} />
        <Typography level="h6">SIP trace</Typography>
        <Typography level="body-sm" textColor="neutral.600" sx={{ mb: 1 }}>
          {registrationLabel(registration)}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          {index?.node && (
            <Chip size="sm" variant="soft" color="neutral">
              node {index.node}
            </Chip>
          )}
          {index?.capturedAt && (
            <Typography level="body-xs" textColor="neutral.500">
              captured {formatTimestamp(index.capturedAt)}
            </Typography>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip
            title="Also show the leg the node placed to the platform, interleaved with the customer's. This is the side that says why a call failed after the caller's PBX was answered."
            placement="top"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography level="body-xs" textColor="neutral.600">
                Platform leg
              </Typography>
              <Switch
                size="sm"
                checked={debug}
                onChange={(e) => setDebug(e.target.checked)}
                slotProps={{ input: { 'aria-label': 'Include the platform leg' } }}
              />
            </Box>
          </Tooltip>
          <Tooltip title="Refetch from the node holding this registration" placement="top">
            <span>
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                aria-label="Refresh trace"
                disabled={indexLoading || !registrationId}
                onClick={loadIndex}
              >
                {indexLoading ? <CircularProgress size="sm" /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <Button
            size="sm"
            variant="plain"
            color="neutral"
            startDecorator={<ContentCopyIcon />}
            disabled={!text}
            onClick={copyText}
          >
            Copy
          </Button>
          <Tooltip title="Download this exchange as a text document" placement="top">
            <span>
              <Button
                size="sm"
                variant="plain"
                color="neutral"
                startDecorator={<DownloadIcon />}
                disabled={!text}
                onClick={downloadText}
              >
                .txt
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Download this exchange as a capture file for Wireshark or sngrep" placement="top">
            <span>
              <Button
                size="sm"
                variant="plain"
                color="neutral"
                startDecorator={pcapPending === 'entry' ? <CircularProgress size="sm" /> : <DownloadIcon />}
                disabled={!selectedId || pcapPending != null}
                onClick={() => downloadPcap('entry')}
              >
                .pcap
              </Button>
            </span>
          </Tooltip>
        </Box>

        {indexError && (
          <Typography level="body-sm" color="danger" sx={{ mb: 1 }}>
            {indexError}
          </Typography>
        )}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '280px minmax(0, 1fr)' },
            gap: 1.5,
          }}
        >
          <Sheet
            variant="outlined"
            sx={{ borderRadius: 'sm', minHeight: 0, overflow: 'auto', maxHeight: { xs: 200, md: 'none' } }}
          >
            {indexLoading && !index && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size="sm" />
              </Box>
            )}
            {!indexLoading && !indexError && entries.length === 0 && (
              <Typography level="body-sm" color="neutral" sx={{ p: 2 }}>
                The node holds no captured exchanges for this registration.
              </Typography>
            )}
            <List size="sm">
              {entries.map((entry) => (
                <ListItem key={entry.id}>
                  <ListItemButton
                    selected={entry.id === selectedId}
                    onClick={() => setSelectedId(entry.id)}
                    sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 0.25 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <Chip size="sm" variant="soft" color={outcomeColor(entry.outcome)}>
                        {entry.outcome || entry.kind || 'exchange'}
                      </Chip>
                      {entry.pinned && (
                        <Tooltip title="Retained regardless of rotation" placement="top">
                          <Chip size="sm" variant="outlined" color="neutral">
                            pinned
                          </Chip>
                        </Tooltip>
                      )}
                    </Box>
                    <Typography level="body-xs" sx={{ fontFamily: 'ui-monospace, monospace' }}>
                      {entryLabel(entry)}
                    </Typography>
                    <Typography level="body-xs" textColor="neutral.500">
                      {formatTimestamp(entry.startedAt)}
                      {typeof entry.messages === 'number' ? ` · ${entry.messages} msg` : ''}
                      {/* Counted by the node whether or not the platform leg was
                          asked for, so this says up front whether turning the
                          toggle on has anything to show — and a bridged call
                          with none is itself the finding. */}
                      {entry.internalMessages ? ` · ${entry.internalMessages} platform` : ''}
                      {typeof entry.rttMs === 'number' ? ` · ${Math.round(entry.rttMs)} ms` : ''}
                    </Typography>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Sheet>

          <Sheet
            variant="outlined"
            sx={{ borderRadius: 'sm', minHeight: 0, overflow: 'auto', p: 1.5, bgcolor: 'background.level1' }}
          >
            {detailLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size="sm" />
              </Box>
            )}
            {!detailLoading && detailError && (
              <Typography level="body-sm" color="danger">
                {detailError}
              </Typography>
            )}
            {!detailLoading && !detailError && !text && (
              <Typography level="body-sm" color="neutral">
                Select an exchange to read its SIP messages.
              </Typography>
            )}
            {!detailLoading && !detailError && text && (
              <Box
                component="pre"
                sx={{
                  m: 0,
                  fontFamily: 'ui-monospace, monospace',
                  fontSize: '0.75rem',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {text}
              </Box>
            )}
          </Sheet>
        </Box>

        {(keepalive || evicted) && (
          <Box sx={{ mt: 1 }}>
            {keepalive && (
              <Typography level="body-xs" textColor="neutral.500">
                Keepalives: sent {keepalive.sent ?? 0}, ok {keepalive.ok ?? 0}, failed {keepalive.failed ?? 0}
                {keepalive.lastCode != null ? ` · last ${keepalive.lastCode}` : ''}
                {typeof keepalive.lastRtt === 'number' ? ` in ${Math.round(keepalive.lastRtt)} ms` : ''}
              </Typography>
            )}
            {evicted && (
              <Typography level="body-xs" textColor="neutral.500">
                {evicted}
              </Typography>
            )}
          </Box>
        )}

        {index && (
          <Box sx={{ mt: 0.5 }}>
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              startDecorator={pcapPending === 'registration' ? <CircularProgress size="sm" /> : <DownloadIcon />}
              disabled={pcapPending != null}
              onClick={() => downloadPcap('registration')}
              sx={{ px: 0 }}
            >
              Download the whole registration as a capture file
            </Button>
          </Box>
        )}
      </ModalDialog>
    </Modal>
  );
}
